/**
 * LegendaryEmblem — the WOW apex (BL-57 ⭐, ART §9). The owner: *"die Legendary-
 * GOLD-Karte muss besonders WOW sein — lass die GPU glühen, hole alles aus der
 * Engine raus."*
 *
 * This is the ONE place a skill card spends real GPU: a genuine **WebGL2 3D
 * emblem** — a procedurally-built, extruded **gold star-sigil** (faces generated
 * in code, no imported mesh) rendered with **metallic Blinn-Phong specular + a
 * fresnel rim under a MOVING light**, so real highlights travel across the gold
 * as it turns. Over it, a self-contained 2D pass adds the **bloom flare** (an
 * additive blurred copy of the hot edges), a **chromatic shimmer** sweep, and
 * drifting **gold particle motes** — the whole thing reads as contraband tech
 * you just intercepted.
 *
 * Justified spend (§6.10): legendary is **rare, single and transient** (≤1 of 3
 * draft cards, briefly on screen), so a per-frame renderer is affordable where
 * the dense arena budget would never allow it. It is fully **isolated** — its OWN
 * WebGL2 context + output canvas, NEVER the arena's `WebGL2Effects`/`UIBloom`
 * (those stay byte-untouched) — **feature-detected** (`create()` returns `null`
 * with no WebGL2 / headless, so the host drops to the enriched-canvas rung D′),
 * **single-instance**, **IntersectionObserver-paused** when offscreen, and
 * **reduced-motion-safe** (a single static lit-gold frame: no spin, no moving
 * light, no motes, no shimmer).
 *
 * Colour is the signed legendary tier `#FFC23D` only — `deriveRamp` gives the
 * metallic core/mid/rim; no new gold hex (guardrail I4).
 */
import { deriveRamp } from "../rendering/EmissiveMaterial.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";

/** The legendary gold tier (signed). Kept here as the single literal the
 *  metallic ramp derives from — NOT a new hue (it is the `RARITY.legendary.mid`
 *  the host also passes; defaulted so the module is self-contained for the gate).
 *  S139 Glacier: re-toned to the new pastel cool amber legendary mid. */
const LEGENDARY_GOLD = "#E8C098";

const VERT_SRC = `#version 300 es
precision highp float;
in vec3 aPos;
in vec3 aNormal;
uniform mat4 uMVP;
uniform mat4 uModel;
uniform mat3 uNormalMat;
out vec3 vNormal;
out vec3 vWorld;
void main() {
  vWorld = (uModel * vec4(aPos, 1.0)).xyz;
  vNormal = normalize(uNormalMat * aNormal);
  gl_Position = uMVP * vec4(aPos, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec3 vNormal;
in vec3 vWorld;
uniform vec3 uLightPos;
uniform vec3 uViewPos;
uniform vec3 uCore;   // near-white gold highlight
uniform vec3 uMid;    // identity gold
uniform vec3 uRim;    // dark gold
out vec4 frag;
void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightPos - vWorld);
  vec3 V = normalize(uViewPos - vWorld);
  vec3 H = normalize(L + V);
  float diff = max(dot(N, L), 0.0);
  float spec = pow(max(dot(N, H), 0.0), 72.0);     // tight metallic highlight
  float fres = pow(1.0 - max(dot(N, V), 0.0), 3.0); // iridescent gold rim
  vec3 col = mix(uRim, uMid, diff);
  col += uCore * spec * 1.6;                         // travelling highlight
  col += uCore * fres * 0.7;                         // hot rim → bloom fuel
  // a touch of ambient so back-facing facets aren't pure black.
  col += uRim * 0.25;
  frag = vec4(col, 1.0);
}`;

interface Mote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  r: number;
}

export class LegendaryEmblem {
  /** The visible canvas (composited: WebGL render + bloom + motes + shimmer). */
  readonly el: HTMLCanvasElement;
  private readonly out: CanvasRenderingContext2D;
  private readonly gl: WebGL2RenderingContext;
  private readonly glCanvas: HTMLCanvasElement;
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vertCount: number;
  private readonly uniforms: Record<string, WebGLUniformLocation | null>;
  private readonly ramp = deriveRamp(LEGENDARY_GOLD);
  private raf = 0;
  private destroyed = false;
  private visible = true;
  private io: IntersectionObserver | null = null;
  private motes: Mote[] = [];
  private t0 = now();
  private size = 96;

  /** Feature-detect + build, or `null` (no WebGL2 / headless) → host uses D′. */
  static create(size = 96): LegendaryEmblem | null {
    if (typeof document === "undefined") return null;
    let gl: WebGL2RenderingContext | null = null;
    try {
      const c = document.createElement("canvas");
      gl = c.getContext("webgl2", {
        alpha: true,
        premultipliedAlpha: false,
        antialias: true,
      });
      if (!gl) return null;
      return new LegendaryEmblem(size, c, gl);
    } catch {
      return null;
    }
  }

  private constructor(
    size: number,
    glCanvas: HTMLCanvasElement,
    gl: WebGL2RenderingContext,
  ) {
    this.size = size;
    this.glCanvas = glCanvas;
    this.gl = gl;

    this.el = document.createElement("canvas");
    this.el.className = "skillcard-emblem-canvas skillcard-emblem-canvas--legendary";
    this.el.setAttribute("aria-hidden", "true");
    const out = this.el.getContext("2d");
    if (!out) throw new Error("no 2d ctx");
    this.out = out;

    // ── build the program ──
    this.program = this.buildProgram();
    this.uniforms = {
      uMVP: gl.getUniformLocation(this.program, "uMVP"),
      uModel: gl.getUniformLocation(this.program, "uModel"),
      uNormalMat: gl.getUniformLocation(this.program, "uNormalMat"),
      uLightPos: gl.getUniformLocation(this.program, "uLightPos"),
      uViewPos: gl.getUniformLocation(this.program, "uViewPos"),
      uCore: gl.getUniformLocation(this.program, "uCore"),
      uMid: gl.getUniformLocation(this.program, "uMid"),
      uRim: gl.getUniformLocation(this.program, "uRim"),
    };

    // ── build the extruded star-sigil geometry ──
    const geo = buildStarPrism(5, 0.45, 0.32);
    this.vertCount = geo.positions.length / 3;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("no vao");
    this.vao = vao;
    gl.bindVertexArray(vao);
    this.attrib("aPos", geo.positions, 3);
    this.attrib("aNormal", geo.normals, 3);
    gl.bindVertexArray(null);

    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
  }

  /** Mount + start. Reduced-motion → a single static frame; else the rAF loop
   *  (paused offscreen via IntersectionObserver). */
  start(): void {
    if (this.destroyed) return;
    if (typeof IntersectionObserver !== "undefined") {
      this.io = new IntersectionObserver((entries) => {
        const e = entries[0];
        if (!e) return;
        this.visible = e.isIntersecting;
        if (this.visible && !isReducedMotion()) this.loop();
      });
      this.io.observe(this.el);
    }
    if (isReducedMotion()) {
      this.renderFrame(0.6); // a flattering static angle, fixed light
      return;
    }
    this.loop();
  }

  private loop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    const tick = (): void => {
      if (this.destroyed || !this.visible || isReducedMotion()) return;
      const t = (now() - this.t0) / 1000;
      this.renderFrame(t);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** One full frame: WebGL sigil → 2D composite (bloom + motes + shimmer). */
  private renderFrame(t: number): void {
    const gl = this.gl;
    const dpr = Math.max(
      1,
      displayDpr(),
    );
    const px = Math.round(this.size * dpr);
    if (this.glCanvas.width !== px) {
      this.glCanvas.width = px;
      this.glCanvas.height = px;
      this.el.width = px;
      this.el.height = px;
    }
    gl.viewport(0, 0, px, px);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    const reduced = isReducedMotion();
    const spin = reduced ? 0.6 : t * 0.9;
    const tilt = reduced ? 0.35 : Math.sin(t * 0.6) * 0.32 + 0.15;

    // matrices
    const model = mul(rotY(spin), rotX(tilt));
    const view = translate(0, 0, -3.4);
    const proj = perspective(Math.PI / 4, 1, 0.1, 100);
    const mvp = mul(proj, mul(view, model));
    const normalMat = mat3FromMat4(model); // pure rotation → no inverse needed

    gl.useProgram(this.program);
    gl.uniformMatrix4fv(this.uniforms["uMVP"]!, false, mvp);
    gl.uniformMatrix4fv(this.uniforms["uModel"]!, false, model);
    gl.uniformMatrix3fv(this.uniforms["uNormalMat"]!, false, normalMat);
    // a moving light → travelling highlights (static when reduced).
    const la = reduced ? 0.9 : t * 1.3;
    gl.uniform3f(this.uniforms["uLightPos"]!, Math.cos(la) * 3, 2.2, Math.sin(la) * 2 + 2.5);
    gl.uniform3f(this.uniforms["uViewPos"]!, 0, 0, 3.4);
    setColor(gl, this.uniforms["uCore"]!, this.ramp.core);
    setColor(gl, this.uniforms["uMid"]!, this.ramp.mid);
    setColor(gl, this.uniforms["uRim"]!, this.ramp.rim);

    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, this.vertCount);
    gl.bindVertexArray(null);

    // ── 2D composite: base + additive bloom flare + shimmer + motes ──
    const out = this.out;
    const W = this.el.width;
    const H = this.el.height;
    out.setTransform(1, 0, 0, 1, 0, 0);
    out.clearRect(0, 0, W, H);
    out.globalCompositeOperation = "source-over";
    out.drawImage(this.glCanvas, 0, 0);

    // heavy gold bloom flare — two additive blurred copies of the hot sigil.
    out.save();
    out.globalCompositeOperation = "lighter";
    if (typeof out.filter !== "undefined") {
      out.filter = `blur(${Math.round(3 * dpr)}px)`;
      out.globalAlpha = 0.85;
      out.drawImage(this.glCanvas, 0, 0);
      out.filter = `blur(${Math.round(8 * dpr)}px)`;
      out.globalAlpha = 0.55;
      out.drawImage(this.glCanvas, 0, 0);
      out.filter = "none";
    } else {
      // filter-less fallback: a single offset additive copy (still flares).
      out.globalAlpha = 0.4;
      out.drawImage(this.glCanvas, 0, 0);
    }
    out.restore();

    if (!reduced) {
      this.drawShimmer(out, W, H, t, dpr);
      this.drawMotes(out, W, H, dpr);
    }
  }

  /** A chromatic/iridescent band sweeping diagonally across the sigil. */
  private drawShimmer(
    out: CanvasRenderingContext2D,
    W: number,
    H: number,
    t: number,
    dpr: number,
  ): void {
    const sweep = ((t * 0.4) % 1) * (W + H) - H;
    out.save();
    // BL-296 — `source-atop`, NOT `lighter`. The sweep is a full-canvas
    // `fillRect`, so an additive blend lit the whole square: every legendary card
    // carried a hard-edged rectangular halo behind its sigil (visible on the
    // draft card, and more so once the hero window grew). Atop paints only where
    // the sigil and its bloom flare already are, so the band follows the shape.
    out.globalCompositeOperation = "source-atop";
    const g = out.createLinearGradient(sweep, 0, sweep + 60 * dpr, H);
    g.addColorStop(0, "transparent");
    g.addColorStop(0.5, this.ramp.core);
    g.addColorStop(1, "transparent");
    out.fillStyle = g;
    out.globalAlpha = 0.18;
    out.fillRect(0, 0, W, H);
    out.restore();
  }

  /** Drifting gold particle motes (additive, bloomed by their own glow). */
  private drawMotes(
    out: CanvasRenderingContext2D,
    W: number,
    H: number,
    dpr: number,
  ): void {
    // spawn
    if (this.motes.length < 18 && Math.random() < 0.4) {
      this.motes.push({
        x: W * (0.35 + Math.random() * 0.3),
        y: H * 0.5,
        vx: (Math.random() - 0.5) * 0.4 * dpr,
        vy: -(0.3 + Math.random() * 0.6) * dpr,
        life: 0,
        max: 60 + Math.random() * 60,
        r: (1 + Math.random() * 2) * dpr,
      });
    }
    out.save();
    out.globalCompositeOperation = "lighter";
    this.motes = this.motes.filter((m) => {
      m.life++;
      m.x += m.vx;
      m.y += m.vy;
      m.vy *= 0.99;
      if (m.life >= m.max) return false;
      const a = Math.sin((m.life / m.max) * Math.PI) * 0.8;
      const g = out.createRadialGradient(m.x, m.y, 0, m.x, m.y, m.r * 4);
      g.addColorStop(0, this.ramp.core);
      g.addColorStop(1, "transparent");
      out.fillStyle = g;
      out.globalAlpha = a;
      out.beginPath();
      out.arc(m.x, m.y, m.r * 4, 0, Math.PI * 2);
      out.fill();
      return true;
    });
    out.restore();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.io?.disconnect();
    this.io = null;
    const gl = this.gl;
    try {
      gl.deleteProgram(this.program);
      gl.deleteVertexArray(this.vao);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    } catch {
      /* context already gone */
    }
    this.el.remove();
  }

  // ─── GL helpers ────────────────────────────────────────────────────────────

  private buildProgram(): WebGLProgram {
    const gl = this.gl;
    const vs = this.shader(gl.VERTEX_SHADER, VERT_SRC);
    const fs = this.shader(gl.FRAGMENT_SHADER, FRAG_SRC);
    const p = gl.createProgram();
    if (!p) throw new Error("no program");
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(p) ?? "link failed");
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return p;
  }

  private shader(type: number, src: string): WebGLShader {
    const gl = this.gl;
    const s = gl.createShader(type);
    if (!s) throw new Error("no shader");
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(s) ?? "compile failed");
    }
    return s;
  }

  private attrib(name: string, data: number[], size: number): void {
    const gl = this.gl;
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(this.program, name);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
  }
}

// ─── Geometry: an extruded N-point star prism (front + back + side walls) ──────

function buildStarPrism(
  points: number,
  innerRatio: number,
  halfDepth: number,
): { positions: number[]; normals: number[] } {
  const rim: [number, number][] = [];
  for (let i = 0; i < points * 2; i++) {
    const a = -Math.PI / 2 + (i / (points * 2)) * Math.PI * 2;
    const r = i % 2 === 0 ? 1 : innerRatio;
    rim.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  const n = rim.length;
  const positions: number[] = [];
  const normals: number[] = [];
  const push = (p: number[], nm: number[]): void => {
    positions.push(p[0]!, p[1]!, p[2]!);
    normals.push(nm[0]!, nm[1]!, nm[2]!);
  };

  // front face (z = +d), normal +Z; back face (z = -d), normal -Z.
  for (let i = 0; i < n; i++) {
    const a = rim[i]!;
    const b = rim[(i + 1) % n]!;
    // front (CCW)
    push([0, 0, halfDepth], [0, 0, 1]);
    push([a[0], a[1], halfDepth], [0, 0, 1]);
    push([b[0], b[1], halfDepth], [0, 0, 1]);
    // back (CW so it faces -Z)
    push([0, 0, -halfDepth], [0, 0, -1]);
    push([b[0], b[1], -halfDepth], [0, 0, -1]);
    push([a[0], a[1], -halfDepth], [0, 0, -1]);
  }

  // side walls — two triangles per rim segment, outward face normal.
  for (let i = 0; i < n; i++) {
    const a = rim[i]!;
    const b = rim[(i + 1) % n]!;
    const fa = [a[0], a[1], halfDepth];
    const fb = [b[0], b[1], halfDepth];
    const ba = [a[0], a[1], -halfDepth];
    const bb = [b[0], b[1], -halfDepth];
    // outward normal = edge × depth
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    let nx = ey;
    let ny = -ex;
    const len = Math.hypot(nx, ny) || 1;
    nx /= len;
    ny /= len;
    const nm = [nx, ny, 0];
    push(fa, nm);
    push(ba, nm);
    push(bb, nm);
    push(fa, nm);
    push(bb, nm);
    push(fb, nm);
  }

  return { positions, normals };
}

// ─── Tiny mat4/mat3 helpers (column-major, WebGL order) ───────────────────────

function mul(a: number[], b: number[]): number[] {
  const o = new Array(16).fill(0);
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
      o[c * 4 + r] = s;
    }
  }
  return o;
}

function rotY(a: number): number[] {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1];
}

function rotX(a: number): number[] {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1];
}

function translate(x: number, y: number, z: number): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1];
}

function perspective(
  fovy: number,
  aspect: number,
  near: number,
  far: number,
): number[] {
  const f = 1 / Math.tan(fovy / 2);
  const nf = 1 / (near - far);
  return [
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (far + near) * nf, -1,
    0, 0, 2 * far * near * nf, 0,
  ];
}

/** Upper-left 3×3 of a mat4 (valid normal matrix for pure rotation). */
function mat3FromMat4(m: number[]): number[] {
  return [m[0]!, m[1]!, m[2]!, m[4]!, m[5]!, m[6]!, m[8]!, m[9]!, m[10]!];
}

function setColor(
  gl: WebGL2RenderingContext,
  loc: WebGLUniformLocation | null,
  hex: string,
): void {
  const h = hex.replace(/^#/, "");
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, "$1$1") : h, 16);
  gl.uniform3f(loc, ((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
